import { createContext, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";

export type ColorMode = "light" | "dark" | "system";
export const COLOR_MODES: readonly ColorMode[] = ["light", "dark", "system"];

// The colour palettes defined as [data-preset="…"] blocks in theme.css.
// ADD A PALETTE: add the [data-preset] (+ dark) block in theme.css, then add its
// key here — that is all the framework needs to expose it in the switcher.
export const PRESETS = ["ede", "canonical", "graphite", "arctic", "emerald"] as const;
export type Preset = (typeof PRESETS)[number];

const DEFAULT_MODE: ColorMode = "system";
const DEFAULT_PRESET: Preset = "ede";
const MODE_KEY = "ede.theme.mode";
const PRESET_KEY = "ede.theme.preset";

interface ThemeState {
    mode: ColorMode;
    preset: Preset;
}

export interface ThemeManager extends ThemeState {
    resolvedMode: "light" | "dark";
    modes: readonly ColorMode[];
    presets: readonly Preset[];
    setMode(mode: ColorMode): void;
    setPreset(preset: Preset): void;
}

const ThemeContext = createContext<ThemeManager | null>(null);

function readStored<T extends string>(key: string, allowed: readonly T[], fallback: T): T {
    if (typeof localStorage === "undefined") return fallback;
    const value = localStorage.getItem(key);
    return value && (allowed as readonly string[]).includes(value) ? (value as T) : fallback;
}

function systemPrefersDark(): boolean {
    return typeof window !== "undefined" && !!window.matchMedia?.("(prefers-color-scheme: dark)").matches;
}

export function ThemeProvider({ children }: { children: ReactNode }) {
    const [state, setState] = useState<ThemeState>(() => ({
        mode: readStored(MODE_KEY, COLOR_MODES, DEFAULT_MODE),
        preset: readStored(PRESET_KEY, PRESETS, DEFAULT_PRESET),
    }));
    const [systemDark, setSystemDark] = useState(systemPrefersDark);

    useEffect(() => {
        if (typeof window === "undefined" || !window.matchMedia) return;
        const mq = window.matchMedia("(prefers-color-scheme: dark)");
        const onChange = () => setSystemDark(mq.matches);
        mq.addEventListener("change", onChange);
        return () => mq.removeEventListener("change", onChange);
    }, []);

    const resolvedMode: "light" | "dark" =
        state.mode === "system" ? (systemDark ? "dark" : "light") : state.mode;

    // Palette applies everywhere (consistent branding across all routes).
    useEffect(() => {
        document.documentElement.setAttribute("data-preset", state.preset);
    }, [state.preset]);

    // Public / auth pages are ALWAYS light. The workspace (/wc) scopes the user's
    // chosen mode via useWorkspaceColorMode(); this sets the light default.
    useEffect(() => {
        document.documentElement.setAttribute("data-theme", "light");
    }, []);

    const value = useMemo<ThemeManager>(
        () => ({
            ...state,
            resolvedMode,
            modes: COLOR_MODES,
            presets: PRESETS,
            setMode: (mode) => {
                setState((s) => ({ ...s, mode }));
                if (typeof localStorage !== "undefined") localStorage.setItem(MODE_KEY, mode);
            },
            setPreset: (preset) => {
                setState((s) => ({ ...s, preset }));
                if (typeof localStorage !== "undefined") localStorage.setItem(PRESET_KEY, preset);
            },
        }),
        [state, resolvedMode],
    );

    return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeManager {
    const ctx = useContext(ThemeContext);
    if (!ctx) throw new Error("useTheme must be used within a ThemeProvider");
    return ctx;
}

// Applies the user's chosen color mode (light/dark/system) for as long as the
// caller is mounted — i.e. the workspace. On unmount it reverts to light, so
// every page outside /wc stays light. Call once near the top of the /wc shell.
export function useWorkspaceColorMode(): void {
    const { resolvedMode } = useTheme();
    useEffect(() => {
        const root = document.documentElement;
        root.setAttribute("data-theme", resolvedMode);
        return () => root.setAttribute("data-theme", "light");
    }, [resolvedMode]);
}
